import React, { useState, useEffect, useCallback, useContext, createContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupabase } from '../../lib/SupabaseProvider';
import { useOrg } from '../../lib/OrgProvider';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { listInterviewSessions, listInterviewSessionsByOrg } from '../../services/interviewService';
import { CollapsibleSidebar } from './CollapsibleSidebar';
import { GraceBanner } from './GraceBanner';
import { OnboardingSplash } from '../onboarding/OnboardingSplash';
import { FeatureLearningProvider } from '../learning/FeatureLearningProvider';
import BrandedLineLoader from '../BrandedLineLoader';

interface AdminLayoutContextValue {
  /** Re-fetch sidebar sessions (e.g. after a delete). */
  refreshSidebar: () => void;
  /** Patch a committed rename without changing the sidebar's loading state. */
  updateSidebarSessionTitle: (sessionId: string, title: string) => void;
}

const AdminLayoutContext = createContext<AdminLayoutContextValue>({
  refreshSidebar: () => {},
  updateSidebarSessionTitle: () => {},
});

/** Access the AdminLayout context (e.g. to refresh the sidebar after mutations). */
export const useAdminLayout = () => useContext(AdminLayoutContext);

interface AdminLayoutProps {
  children: React.ReactNode;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const { user } = useSupabase();
  const { activeOrg, onboardingState, isLoading: orgLoading } = useOrg();
  const [sidebarSessions, setSidebarSessions] = useState<Array<{ id: string; title: string; date: string; brand_id?: string | null }>>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  /** Keep existing recent rows, ordering and workspace membership on rename. */
  const updateSidebarSessionTitle = useCallback((sessionId: string, title: string) => {
    setSidebarSessions(previous => {
      const index = previous.findIndex(session => session.id === sessionId);
      if (index === -1 || previous[index].title === title) return previous;
      return previous.map((session, row) => row === index ? { ...session, title } : session);
    });
  }, []);

  const loadSessions = useCallback(async () => {
    // Skip session fetch while org provider is still initializing (orgLoading)
    // or during onboarding - prevents user-scoped fetch before activeOrg resolves.
    if (!user?.id || orgLoading || onboardingState.status !== 'none') return;
    setLoading(true);
    setFetchError(false);
    try {
      // Use org-scoped query when an active org is set, fallback to user-scoped
      const data = activeOrg?.id
        ? await listInterviewSessionsByOrg(activeOrg.id)
        : await listInterviewSessions(user.id);
      const formatted = data.slice(0, 20).map((session) => ({
        id: session.id,
        title: session.target_name || 'Untitled',
        date: new Date(session.created_at).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        }),
        brand_id: session.brand_id ?? null,
      }));
      setSidebarSessions(formatted);
    } catch (err) {
      captureException(err instanceof Error ? err : String(err), 'AdminLayout.fetchSessions', { userId: user?.id, orgId: activeOrg?.id });
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [user?.id, activeOrg?.id, orgLoading, onboardingState.status]);

  useEffect(() => {
    if (!user?.id || orgLoading) return;
    void loadSessions();
  }, [user?.id, activeOrg?.id, orgLoading, onboardingState.status, loadSessions]);

  // Brand the browser tab as the dashboard while inside /admin/*.
  // index.html keeps a neutral "holito" title for respondent-facing routes.
  useEffect(() => {
    const previous = document.title;
    document.title = 'Dashboard · holito';
    return () => {
      document.title = previous;
    };
  }, []);

  const handleSessionClick = (sessionId: string) => {
    void navigate(`/admin/interviews/${sessionId}`);
  };

  // Loading shell while org provider initializes. Same BrandedLineLoader
  // on `bg-background` as ProtectedRoute and the index.html boot loader so the
  // refresh-into-admin path looks like one continuous bar, not three
  // distinct loading states.
  if (orgLoading) {
    return (
      <div className="h-screen bg-background flex items-center justify-center">
        <BrandedLineLoader logoUrl={null} />
      </div>
    );
  }

  // Normal admin layout - onboarding modal overlays on top with blurred backdrop
  return (
    <div className="h-screen bg-background flex overflow-hidden">
      <CollapsibleSidebar
        recentSessions={sidebarSessions}
        onSessionClick={handleSessionClick}
        loading={loading}
        fetchError={fetchError}
      />
      <AdminLayoutContext.Provider value={{ refreshSidebar: loadSessions, updateSidebarSessionTitle }}>
        <main className="flex-1 overflow-y-auto overflow-x-hidden md:ml-0" style={{ scrollbarGutter: 'stable' }}>
          {/* Seat-limit grace banner - silent when not in grace period. It
              owns its own spacing, so a hidden banner reserves zero space
              above the page header (no empty padded wrapper here). */}
          <GraceBanner />
          <FeatureLearningProvider userId={user?.id ?? null}>{children}</FeatureLearningProvider>
        </main>
      </AdminLayoutContext.Provider>

      <OnboardingSplash />
    </div>
  );
};
